import clsx from 'clsx';
import { useStore } from 'effector-react';
import { ChangeEventHandler, FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { useLocation } from 'react-router-dom';

import styles from './items-without-filters.module.scss';

import { IItemsWithoutFiltersProps } from './types';

import { Item, QueryNotFound } from '..';
import { Breadcrumbs, Loader, Pagination, Paragraph, SortSelect, Title } from '../ui';

import {
  $catalogCommonStore,
  $currentDataWithoutFiltersStore,
  getCatalogCommonFx,
  removeCommonItems,
  removeCurrentDataWithoutFilters,
  saveCurrentDataWithoutFilters,
  saveCurrentPaginationPageWithoutFilters,
  saveCurrentSortWithoutFilters,
  updateCatalogCommonBtnFx,
  updateCommonDataImperative,
} from 'src/models';
import { TSortingItems, getSearch, handleScroll } from '~utils';

export const ItemsWithoutFilters: FC<IItemsWithoutFiltersProps> = memo(
  ({ title, fetchFn, additionalQuery = '', className = '', isSearchPage = false, ...rest }) => {
    const { data: currData, status } = useStore($catalogCommonStore);
    const prevData = useStore($currentDataWithoutFiltersStore);

    const { search } = useLocation();
    const q = useMemo(() => search.split('=')[1], [search]);
    const key = globalThis.location.pathname.split('/').at(-1) + (q ? `?q=${q}` : '');

    const data = currData ?? prevData[key]?.data;
    const currSort = prevData[key]?.sort ?? '-is_hit';

    const sortRef = useRef<TSortingItems>(currSort);

    const handleSortingChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
      e => {
        saveCurrentSortWithoutFilters({ path: key, sort: e.target.value as TSortingItems });
        saveCurrentPaginationPageWithoutFilters({ path: key, page: 1 });
      },
      [key],
    );

    const handleSetPage = (path: string) => (page: number) => {
      saveCurrentPaginationPageWithoutFilters({ path, page });
    };

    useEffect(() => {
      const preventData = prevData[key]?.data;
      if (preventData && sortRef.current === currSort) {
        updateCommonDataImperative(preventData);
        removeCurrentDataWithoutFilters(key);
        return;
      }

      // нужно, чтобы после возвращения из карточки сортировка работала
      sortRef.current = currSort;

      removeCurrentDataWithoutFilters(key);

      if (isSearchPage) {
        getCatalogCommonFx({ q, queries: [`sort=${currSort}`, additionalQuery], fn: getSearch });
        handleScroll();
      } else {
        getCatalogCommonFx({
          queries: [`sort=${currSort}`, additionalQuery],
          fn: fetchFn,
        });
      }
    }, [currSort, additionalQuery, fetchFn, isSearchPage, q, key]);

    useEffect(() => () => removeCommonItems(), []);

    if (!data) return <Loader />;

    return (
      <div className={clsx(styles.container, className)} {...rest}>
        <Breadcrumbs />
        <div className={styles.title_box}>
          <Title className={styles.title}>{title}</Title>
          {q && (
            <Paragraph className={styles.title_q} isGradient>
              {decodeURI(q)}
            </Paragraph>
          )}
        </div>

        {!!data?.data.length && (
          <SortSelect value={currSort} onChange={handleSortingChange} className={styles.sort_box} />
        )}
        {data.data.length ? (
          <ul className={styles.list}>
            {data.data.map(item => (
              <Item
                key={item.id}
                data={item}
                isCartButton
                handleClick={() => {
                  saveCurrentDataWithoutFilters({
                    data,
                    path: key,
                  });
                }}
              />
            ))}
          </ul>
        ) : (
          <QueryNotFound className={styles.not_found} />
        )}
        {data.meta.pagination.num_pages !== 1 && (
          <Pagination
            className={styles.pagination}
            amountPages={data.meta.pagination.num_pages}
            currSort={currSort}
            onClick={getCatalogCommonFx}
            onBtnLoadClick={updateCatalogCommonBtnFx}
            additionalQuery={additionalQuery}
            q={q}
            callback={isSearchPage ? getSearch : fetchFn}
            setPage={handleSetPage(key)}
          />
        )}
        {status === 'LOADING' && <Loader withOverlay />}
      </div>
    );
  },
);
